import { getEnv, RestHandler, getCsrfToken } from '@ctct/ctct-helpers';

import { getAccountData, getCustomerAudit } from './DistUI';

const getBaseUrl = () => {
  if (getEnv() === 'local') {
    return 'https://localhost:9000/v1/myaccountapi';
  }

  return `https://app${getEnv()}constantcontact.com/v1/myaccountapi`;
};

const options = { headers: {} };
const token = getCsrfToken();

if (getEnv() !== 'local') {
  options.headers['X-CSRF-Token'] = token;
}

const restHandler = new RestHandler(getBaseUrl(), options);

const DEFAULT_OPTIONS = {
  mode: 'cors',
  credentials: 'same-origin',
  cache: 'no-store',
  redirect: 'follow',
  headers: {
    'X-CSRF-Token': token,
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json',
  },
};

const getPlanRating = async (params) => {
  const accountData = await getAccountData();
  const {
    contactUsed: used,
    currentPlanId,
    customer,
    discount: { discountDescription: description, discountPercent } = {},
    discountCode,
    getActivePromotionCodes,
    paramPlanGroupId,
    newAddOnServices,
    paymentPlanGroupId: paymentPlanId,
    selectedCurrency: currencyCode,
    newPlanType,
    selectedPlan: { id: newPlanId } = {},
    smsUsed,
  } = params;
  const { planDetail: { planGroupId } = {} } = accountData;
  const usageData = [{ usageType: 'CONTACTS', used: String(used || 0), resetValue: 0, minValue: null }];

  if (smsUsed != null) {
    usageData.push({ usageType: 'SMS', used: smsUsed, resetValue: 0, minValue: null });
  }

  const postBody = {
    includeTax: !!customer && !customer.vatNumber,
    customer,
    currencyCode,
    planGroupId: paramPlanGroupId || planGroupId,
    currentPlanId,
    newPlanType,
    newPlanId,
    newAddOnServices,
    paymentPeriod: 0,
    paymentPlanId,
    usageData,
  };

  if (getActivePromotionCodes) {
    postBody.activePromotionCodes = discountCode ? [discountCode] : [];
  } else {
    postBody.newPromotionCodes = discountCode ? [discountCode] : [];
  }

  try {
    const response = await fetch(`${getBaseUrl()}/billing/customers/plan/rating`, {
      method: 'POST',
      ...DEFAULT_OPTIONS,
      body: JSON.stringify(postBody),
    });
    const rating = await response.json();

    return {
      ...rating,
      description,
      name: 'paymentPlan',
      discountPercent,
    };
  } catch (err) {
    return err;
  }
};

const getPlanGroup = async (currency, count = 0, usage = 0) => {
  const audit = await getCustomerAudit();

  if (audit) {
    const { createdOn } = audit;
    const currentTime = new Date().getTime();

    // More than 5 minutes ago account created.
    if (currentTime - createdOn > 60000 * 5) {
      return restHandler.get(`/plan/customers/users/currency/${currency}/plan_group?count=${count}`);
    }

    return restHandler.get(`/plan/customers/users/currency/${currency}/plan_group?count=${count}&usage=${usage}`);
  }

  throw new Error('DIST UI error');
};

const getBillingInfo = () => restHandler.get('/billing/customers/users/billinginfo');

const getCancelReasons = () => restHandler.get('/billing/cancelreasons');

const getRMCValue = (key) => restHandler.get(`/billing/utils/${key}/configurationvalue`);

const onlineCancel = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/online_cancel?updateBy=customer`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

const onlineCancelVoid = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/online_cancel/void`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

const setPendingSuspend = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/setpendingsuspend`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

const getCustomerInfo = ({ customerInfo, customerId, currency }) => {
  const {
    first_name: firstName,
    last_name: lastName,
    company_name: organizationName,
    email: contactEmail,
    city,
    address1,
    address2,
    phone: contactPhone,
    state,
    country,
    postal_code: zipcode,
    vatNumber,
    billingPeriod,
  } = customerInfo;

  return {
    customerId,
    userUuid: null,
    firstname: firstName,
    lastname: lastName,
    companyname: organizationName,
    email: contactEmail,
    phone: contactPhone,
    address1,
    address2,
    city,
    state,
    country,
    zipcode,
    contactEmail,
    vatNumber,
    billingType: {
      name: 'R',
    },
    customerInSingleBilling: false,
    billingLetterOption: 'Y',
    currencyCd: currency,
    billingPeriod,
    billingDayOfMonth: 0,
    addressValidationRequired: true,
    noPrepayAutoRenewOptin: 0,
    addressProvided: true,
    singleBilledAccount: false,
    partnerBilledAccount: false,
  };
};

const payForPlan = (data) => {
  const newToken = getCsrfToken();

  return fetch(`${getBaseUrl()}/billing/customers/users/plan/pay`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    headers: {
      ...DEFAULT_OPTIONS.headers,
      'X-CSRF-Token': newToken,
    },
    body: JSON.stringify(data),
  });
};

const cancelAddonSubscription = (data) => {
  return fetch(`${getBaseUrl()}/billing/customers/canceladdonsubscription`, {
    method: 'POST',
    ...DEFAULT_OPTIONS,
    body: JSON.stringify(data),
  });
};

export default {
  getBillingInfo,
  getCancelReasons,
  getRMCValue,
  onlineCancel,
  onlineCancelVoid,
  setPendingSuspend,
  getPlanRating,
  getPlanGroup,
  getCustomerInfo,
  payForPlan,
  cancelAddonSubscription,
};
